import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { RequestState } from '../../core/enums/requestState';
import { RootState } from '../../core/store/store';
import { GalleryTag } from '../interfaces/gallery-tag.interface';

export interface GalleryTagsState {
  readonly requestStates: {
    readonly fetchGalleryTags: RequestState;
    readonly editGalleryTag: RequestState;
    readonly addNewGalleryTag: RequestState;
    readonly deleteGalleryTag: RequestState;
    readonly restoreGalleryTag: RequestState;
  };
  readonly galleryTags: GalleryTag[];
}

const galleryTagsState: GalleryTagsState = {
  requestStates: {
    fetchGalleryTags: RequestState.IDLE,
    editGalleryTag: RequestState.IDLE,
    addNewGalleryTag: RequestState.IDLE,
    deleteGalleryTag: RequestState.IDLE,
    restoreGalleryTag: RequestState.IDLE,
  },
  galleryTags: [],
};

const galleryTagsSlice = createSlice({
  name: 'galleryTags',
  initialState: galleryTagsState,
  reducers: {
    fetchGalleryTags(state) {
      state.requestStates.fetchGalleryTags = RequestState.PENDING;
    },

    fetchGalleryTagsSuccess(state, { payload }: PayloadAction<{ galleryTags: GalleryTag[] }>) {
      state.requestStates.fetchGalleryTags = RequestState.RESOLVED;
      state.galleryTags = payload.galleryTags;
    },

    fetchGalleryTagsError(state, _: PayloadAction<void>) {
      state.requestStates.fetchGalleryTags = RequestState.REJECTED;
    },

    editGalleryTag(state, _: PayloadAction<{ galleryTagId: number; editedGalleryTagName: string }>) {
      state.requestStates.editGalleryTag = RequestState.PENDING;
    },

    editGalleryTagSuccess(state, { payload }: PayloadAction<{ id: number; editedGalleryTagName: string }>) {
      state.requestStates.editGalleryTag = RequestState.RESOLVED;

      state.galleryTags = state.galleryTags.map((galleryTag: GalleryTag) => {
        if (galleryTag.id === payload.id) {
          galleryTag = { ...galleryTag, name: payload.editedGalleryTagName };
        }
        return galleryTag;
      });
    },

    editGalleryTagError(state, _: PayloadAction<void>) {
      state.requestStates.editGalleryTag = RequestState.REJECTED;
    },

    deleteGalleryTag(state, _: PayloadAction<{ galleryTagId: number }>) {
      state.requestStates.deleteGalleryTag = RequestState.PENDING;
    },

    deleteGalleryTagSuccess(state, { payload }: PayloadAction<{ galleryTagId: number }>) {
      state.requestStates.deleteGalleryTag = RequestState.RESOLVED;

      state.galleryTags = state.galleryTags.map((galleryTag: GalleryTag) => {
        if (galleryTag.id === payload.galleryTagId) {
          galleryTag = {
            ...galleryTag,
            isDeleted: true,
          };
        }
        return galleryTag;
      });
    },

    deleteGalleryTagError(state, _: PayloadAction<void>) {
      state.requestStates.deleteGalleryTag = RequestState.REJECTED;
    },

    restoreGalleryTag(state, _: PayloadAction<{ galleryTagId: number; galleryTagName: string }>) {
      state.requestStates.restoreGalleryTag = RequestState.PENDING;
    },

    restoreGalleryTagSuccess(state, { payload }: PayloadAction<{ galleryTagId: number }>) {
      state.requestStates.restoreGalleryTag = RequestState.RESOLVED;

      state.galleryTags = state.galleryTags.map((galleryTag: GalleryTag) => {
        if (galleryTag.id === payload.galleryTagId) {
          galleryTag = {
            ...galleryTag,
            isDeleted: false,
          };
        }
        return galleryTag;
      });
    },

    restoreGalleryTagError(state, _: PayloadAction<void>) {
      state.requestStates.restoreGalleryTag = RequestState.REJECTED;
    },

    addNewGalleryTag(state, _: PayloadAction<{ newGalleryTagName: string }>) {
      state.requestStates.addNewGalleryTag = RequestState.PENDING;
    },

    addNewGalleryTagSuccess(state, { payload }: PayloadAction<{ newGalleryTag: GalleryTag }>) {
      state.requestStates.addNewGalleryTag = RequestState.RESOLVED;
      state.galleryTags.push(payload.newGalleryTag);
    },

    addNewGalleryTagError(state, _: PayloadAction<void>) {
      state.requestStates.addNewGalleryTag = RequestState.REJECTED;
    },

    clearRequestStates(state) {
      state.requestStates.fetchGalleryTags = RequestState.IDLE;
      state.requestStates.editGalleryTag = RequestState.IDLE;
      state.requestStates.deleteGalleryTag = RequestState.IDLE;
      state.galleryTags = [];
    },

    clearDeleteGalleryTagRequestState(state) {
      state.requestStates.deleteGalleryTag = RequestState.IDLE;
    },

    clearEditGalleryTagRequestState(state) {
      state.requestStates.editGalleryTag = RequestState.IDLE;
    },

    clearAddNewGalleryTagRequestState(state) {
      state.requestStates.addNewGalleryTag = RequestState.IDLE;
    },
  },
});

export const galleryTagsActions = galleryTagsSlice.actions;
export const galleryTagsReducers = galleryTagsSlice.reducer;
export const selectGalleryTagsState = (state: RootState) => state.galleryTags;

export const selectGalleryTags = (state: RootState) => selectGalleryTagsState(state).galleryTags;

export const selectIsGalleryTagsLoading = (state: RootState) =>
  selectGalleryTagsState(state).requestStates.fetchGalleryTags === RequestState.PENDING;

export const selectIsEditGalleryTagLoading = (state: RootState) =>
  selectGalleryTagsState(state).requestStates.editGalleryTag === RequestState.PENDING;

export const selectIsEditGalleryTagResolved = (state: RootState) =>
  selectGalleryTagsState(state).requestStates.editGalleryTag === RequestState.RESOLVED;

export const selectIsDeleteGalleryTagLoading = (state: RootState) =>
  selectGalleryTagsState(state).requestStates.deleteGalleryTag === RequestState.PENDING;

export const selectIsDeleteGalleryTagResolved = (state: RootState) =>
  selectGalleryTagsState(state).requestStates.deleteGalleryTag === RequestState.RESOLVED;

export const selectIsAddNewGalleryTagLoading = (state: RootState) =>
  selectGalleryTagsState(state).requestStates.addNewGalleryTag === RequestState.PENDING;

export const selectIsAddNewGalleryTagResolved = (state: RootState) =>
  selectGalleryTagsState(state).requestStates.addNewGalleryTag === RequestState.RESOLVED;
